/* eslint-disable max-lines */
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import H2 from '@base/H2/H2';
import P1 from '@base/P1/P1';
import H3 from '@base/H3/H3';
import ContentsTable from '@base/ContentsTable/ContentsTable';
import MenusNav from '@base/MenusNav/MenusNav';
import { menus } from '@utils/constants';
import { Spacings, Fonts } from '@styles/index';
import Layout from '@layout/index';
import LeftContainer from '@cont/CompanyDetails/LeftContainer';
import RightContainer from '@cont/CompanyDetails/RightContainer';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';

const ClassroomTermsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: span 24;
`;

const ClassroomTermsContainer = styled.div`
  li {
    font-weight: ${Fonts.REGULAR};
    font-size: ${Fonts.P1};
    line-height: ${Fonts.HEADER_LINE_HEIGHT};
    color: var(--color-text-primary);
  }
`;

const StyledH3 = styled(H3)`
  margin-bottom: ${Spacings.SPACING_6B};
  margin-top: 100px;
`;

const H2Container = styled.div``;

const tableData = [
  { label: '1. DEFINITIONS', id: 'definitions' },
  { label: '2. SERVICES', id: 'services' },
  { label: '3. GRANT OF LICENSE', id: 'grant' },
  {
    label: '4. USE OF SERVICES, YOUR RESPONSIBILITIES AND CONDUCT',
    id: 'conduct'
  },
  { label: '5. END USERS', id: 'endusers' },
  { label: '6. UNACADEMY OBLIGATIONS FOR CONTENT', id: 'content' },
  { label: '7. ELIGIBILITY', id: 'eligibility' },
  { label: '8. TERMINATION', id: 'termination' },
  { label: '9. PROPRIETARY RIGHTS', id: 'rights' },
  { label: '10. COPYRIGHT', id: 'copyright' },
  { label: '11. CONFIDENTIALITY', id: 'confidentiality' },
  { label: '12. INJUNCTIVE RELIEF', id: 'relief' },
  { label: '13. DISCLAIMER AND INDEMNIFICATION', id: 'disclaimer' },
  { label: '14. LIMITATION OF LIABILITY', id: 'limitations' },
  { label: '15. Additional terms', id: 'other' }
];

const ClassroomTerms = () => {
  const router = useRouter();
  const { pathname } = router;

  return (
    <>
      <LeftContainer>
        <MenusNav data={menus} currentHref={pathname} />
      </LeftContainer>

      <RightContainer>
        <ClassroomTermsWrapper>
          <ClassroomTermsContainer>
            <H2Container>
              <H2 as="h1">Unacademy Live Classroom – Terms of Service</H2>
            </H2Container>
            <br />
            <br />
            <ContentsTable data={tableData} />

            <P1>
              Welcome to Unacademy Live Classroom. These terms of service
              (“Service Terms”), are between Unacademy (also referred to as
              “UNACADEMY,” “we,” “us,” or “our”) and you or the entity you
              represent (“you” or “your”). Your use of and access to the website
              and Unacademy Classroom service and associated software
              (collectively, the &quot;Services&quot;) of Sorting Hat
              Technologies Private Limited (&quot;Unacademy&quot;) is
              conditioned upon your compliance with and acceptance of these
              terms, which include your agreement to arbitrate claims.
            </P1>
            <br />
            <P1>
              Please read these Service Terms carefully and thoroughly, along
              with the Terms and Conditions, Privacy Policy and all other rules,
              policies and guidelines made available or published on Unacademy
              Platform and as may be updated by us from time to time as they
              will govern your use of the Service. By registering, browsing, or
              accessing Unacademy website or by utilizing the Classroom Services
              you agree to be bound by these Service Terms and all terms, and
              incorporated policies (the “agreement”). The Classroom Service are
              not available to persons who are not legally eligible to be bound
              by these Service Terms. Unacademy will provide the Services, and
              you may access and use the Services, in accordance with these
              Service Terms.
            </P1>
            <br />
            <P1>
              <b>System Requirements:</b>
              Use of the Services requires one or more compatible devices,
              Internet access, and certain software. and may require obtaining
              updates or upgrades from time to time. Because use of the Services
              involves hardware, software, and Internet access, Your ability to
              access and use the Services may be affected by the performance of
              these factors. High speed Internet access is recommended. You
              acknowledge and agree that such system requirements, which may be
              changed from time to time, are Your responsibility and are to be
              procured at your own cost.
            </P1>

            <a name={tableData[0].id}>
              <StyledH3>{tableData[0].label}</StyledH3>
            </a>
            <ol>
              <li>
                “Classroom Service” shall mean the service provided by Unacademy
                under these Service Terms through which You can conduct virtual
                live classes on the Unacademy Platform by sharing the Classroom
                Link with Your students/learners.
              </li>
              <li>
                Classroom Link” shall mean the link that is generated by You and
                share with any person, who can join the classroom using the said
                link.
              </li>
              <li>
                “Classes” shall mean the virtual live streamed classes conducted
                by You or by Your Representatives.
              </li>
              <li>
                “End User” means a You or Your students/learner to whom you have
                shared the Classroom Link to participant and use the Classroom
                Service.
              </li>
              <li>
                “Laws” means shall mean any statute, law, regulation, ordinance,
                rule, judgment, notification, rule of common law, Order, decree,
                bye-law, government approval, directive, guideline, requirement
                or other governmental restriction, or any similar form of
                decision of, or determination by, or any interpretation, policy
                or administration, having the force of law of any of the
                foregoing, by any authority having jurisdiction over the matter
                in question;
              </li>
              <li>
                “Policies” means Unacademy Terms of Service, Privacy Policy,
                User Guidelines and any other policy, rule, guidelines or terms
                published or undated by Unacademy from time to time on its
                website www.unacademy.com or communicated to you by Unacademy by
                any medium of communication and are hereby incorporated in these
                Service Terms by reference.
              </li>
              <li>
                “Representative” means any person that You have authorized to
                use the Service through Your account.
              </li>
            </ol>

            <a name={tableData[1].id}>
              <StyledH3>{tableData[1].label}</StyledH3>
            </a>
            <P1>
              Service under these Service Terms shall include Classroom Service
              along with the associated features, links and software and
              standard updates to the Services that are made generally available
              by Unacademy during the term (together “Services”). Unacademy may,
              in its sole discretion, discontinue the Services or modify the
              features of the Services from time to time without prior notice.
              Unacademy may, from time to time, offer access to services that
              are classified as Beta version. Access to and use of Beta versions
              may be subject to additional agreements. Unacademy makes no
              representations that a Beta version will ever be made generally
              available and reserves the right to discontinue or modify a Beta
              version at any time without notice. Beta versions are provided AS
              IS, may contain bugs, errors or other defects, and Your use of a
              Beta version is at Your sole risk.
            </P1>
            <a name={tableData[2].id}>
              <StyledH3>{tableData[2].label}</StyledH3>
            </a>
            <P1>
              By Unacademy: Subject to these Service Terms, Privacy Policy,
              Policies and other rules and policies made available or published
              elsewhere, Unacademy hereby grants the you and to the End User a
              non-exclusive, non-transferable, non-sublicensable, limited
              license to use the Services in accordance with these Service
              Terms.
            </P1>
            <br />
            <P1>
              By You: You shall be solely responsible for your own Content and
              the consequences of submitting and publishing such Content through
              the Services. You affirm, represent, and warrant that you own or
              have the necessary licenses, rights, consents, and permissions to
              publish the Content that you submit; and you grant limited license
              to Unacademy to all patent, trademark, trade secret, copyright or
              other proprietary rights in and to such Content for the limited
              purpose of publication, display, recording and streaming of the
              Content through the Services for the duration the said Content is
              available on the Services and provide the access for such Content
              to the End Users to whom you provide the Classroom link or link to
              the classroom recordings.
            </P1>
            <a name={tableData[3].id}>
              <StyledH3>{tableData[3].label}</StyledH3>
            </a>
            <P1>
              You may only use the Services pursuant to the Service Terms. You
              are solely responsible for Your and Your End Users’ use of the
              Services and shall abide by, and ensure compliance with, all Laws
              in connection with Your and each End User’s use of the Services,
              including but not limited to Laws related to recording,
              intellectual property, privacy and export control. Use of the
              Services is void where prohibited.
            </P1>
            <ol>
              <li>
                Registration: You may be required to provide information about
                Yourself in order to register for and/or use the Services. You
                agree that any such information shall be accurate. You may also
                be asked to choose a user name and password. You are entirely
                responsible for maintaining the security of Your user name and
                password and agree not to disclose such to any third party.
              </li>
              <li>
                Your Content and Conduct: You agree that You are solely
                responsible for the content (&quot;Content&quot;) sent or
                transmitted by You or displayed or uploaded by You in using the
                Services and for compliance with all Laws pertaining to the
                Content, including, but not limited to, Copyright law and other
                Laws requiring You to obtain the consent of a third party to use
                the Content and to provide appropriate notices of third party
                rights. You represent and warrant that You have the right to
                upload the Content to Unacademy Classroom and that such use does
                not violate or infringe on any rights of any third party. Under
                no circumstances will Unacademy be liable in any way for any (a)
                Content that is transmitted or viewed while using the Services,
                (b) errors or omissions in the Content, or (c) any loss or
                damage of any kind incurred as a result of the use of, access
                to, or denial of access to Content. Although Unacademy is not
                responsible for any Content, Unacademy may delete any Content,
                at any time without notice to You, if Unacademy becomes aware
                that it violates any provision of these Service Terms, or any
                law. You retain copyright and any other rights You already hold
                in Content which You submit, post or display on or through, the
                Services.
              </li>
              <li>
                Recordings: You are responsible for compliance will all
                recording laws and associated consent requirements. By using the
                Services, you are giving Unacademy consent to store recordings
                for any or all your Classes conducted through the Services, if
                such recordings are stored in our systems. You can share the
                link to the recording of Your Classes to any person at your
                discretion but You shall be responsible for any
                default/misconduct or violation that occurs due to the such
                person. At no point in time shall Unacademy be responsible any
                activities by any users including you, your students or any
                third party.
              </li>
              <li>
                Prohibited Use: You agree that You will not use, and will not
                permit any End User to use, the Services to: (i) modify,
                disassemble, decompile, prepare derivative works of, reverse
                engineer or otherwise attempt to gain access to the source code
                of the Services; (ii) knowingly or negligently use the Services
                in a way that abuses, interferes with, or disrupts Unacademy’s
                networks, Your accounts, or the Services; (iii) engage in
                activity that is illegal, fraudulent, false, or misleading, (iv)
                transmit through the Services any material that may infringe the
                intellectual property or other rights of third parties; (v)
                build or benchmark a competitive product or service, or copy any
                features, functions or graphics of the Services; or (vi) use the
                Services to communicate any message or material that is
                derogatory to any religion, caste, sex or political opinions,
                harassing, libelous, threatening, obscene, indecent, would
                violate the intellectual property rights of any party or is
                otherwise unlawful, that would give rise to civil liability, or
                that constitutes or encourages conduct that could constitute a
                criminal offense, under any applicable law or regulation; (vii)
                upload or transmit any software, Content or code that does or is
                intended to harm, disable, destroy or adversely affect
                performance of the Services in any way or which does or is
                intended to harm or extract information or data from other
                hardware, software or networks of Unacademy or other users of
                Services; (viii) engage in any activity or use the Services in
                any manner that could damage, disable, overburden, impair or
                otherwise interfere with or disrupt the Services, or any servers
                or networks connected to the Services or Unacademy&apos;s
                security systems. (ix) use the Services in violation of any
                Unacademy policy or in a manner that violates applicable law,
                including but not limited to anti-spam, export control, privacy,
                and anti-terrorism laws and regulations and laws requiring the
                consent of subjects of audio and video recordings, and You agree
                that You are solely responsible for compliance with all such
                laws and regulations.
              </li>
              <li>
                Limitations on Use: You agree to use the Services only for
                educational purposes and not use the Service for any other
                purpose.
              </li>
            </ol>
            <a name={tableData[4].id}>
              <StyledH3>{tableData[4].label}</StyledH3>
            </a>
            <P1>
              You are responsible for the activities of all End Users who access
              or use the Services through your account and you agree to ensure
              that any such End User will comply with these Service Terms and
              any & all Unacademy policies. Unacademy assumes no responsibility
              or liability for violations. If You become aware of any violation
              of these Service Terms in connection with use of the Services by
              any person, please contact Unacademy at report@unacademy.com.
              Unacademy may investigate any complaints and violations that come
              to its attention and may take any (or no) action that it believes
              is appropriate, including, but not limited to issuing warnings,
              removing the content or terminating accounts and/or User profiles.
              Under no circumstances will Unacademy be liable in any way for any
              data or other content viewed while using the Services, including,
              but not limited to, any errors or omissions in any such data or
              content, or any loss or damage of any kind incurred as a result of
              the use of, access to, or denial of access to any data or content.
            </P1>
            <a name={tableData[5].id}>
              <StyledH3>{tableData[5].label}</StyledH3>
            </a>
            <P1>
              Unacademy will maintain reasonable physical and technical
              safeguards to prevent unauthorized disclosure of or access to
              Content, in accordance with industry standards. Unacademy will
              notify You if it becomes aware of unauthorized access to Content.
              Unacademy will not access, view or process Content except (a) as
              provided for in these Service Terms and in Unacademy’s Privacy
              Policy; (b) as authorized or instructed by You, (c) as required to
              perform its obligations under these Service Terms; or (d) as
              required by Law. Unacademy has no other obligations with respect
              to Content.
            </P1>
            <a name={tableData[6].id}>
              <StyledH3>{tableData[6].label}</StyledH3>
            </a>
            <P1>
              You affirm that You are of legal age and are otherwise fully able
              and competent to enter into the terms, conditions, obligations,
              affirmations, representations, and warranties set forth in these
              Service Terms, and to abide by and comply with these Service
              Terms. Although we cannot absolutely control whether minors gain
              unauthorized access to the Services, access may be terminated
              without warning if we believe that You are underage or otherwise
              ineligible.
            </P1>
            <a name={tableData[7].id}>
              <StyledH3>{tableData[7].label}</StyledH3>
            </a>
            <P1>
              If Unacademy becomes aware or has reason to believe that you are
              in violation of these Service Terms or any other Polices then
              Unacademy reserves the right to terminate your account with or
              without providing any notice or reason. If you wish to terminate
              your account or any recordings you can do the same by writing to
              us at legal@unacademy.com.
            </P1>
            <a name={tableData[8].id}>
              <StyledH3>{tableData[8].label}</StyledH3>
            </a>
            <P1>
              Unacademy retain ownership of all proprietary rights in the
              Services and in all trade names, trademarks, service marks, logos,
              and domain names (&quot;Unacademy Marks&quot;) associated or
              displayed with the Services. You may not frame or utilize framing
              techniques to enclose any Unacademy Marks, or other proprietary
              information (including images, text, page layout, or form) of
              Unacademy without express written consent. You may not use any
              meta tags or any other &quot;hidden text&quot; utilizing Unacademy
              Marks without Unacademy&apos;s express written consent.
            </P1>
            <a name={tableData[9].id}>
              <StyledH3>{tableData[9].label}</StyledH3>
            </a>
            <P1>
              You may not post, modify, distribute, or reproduce in any way
              copyrighted material, trademarks, rights of publicity or other
              proprietary rights without obtaining the prior written consent of
              the owner of such proprietary rights. Unacademy may deny access to
              the Services to any User who is alleged to infringe another
              party&apos;s copyright. Without limiting the foregoing, if You
              believe that Your copyright has been infringed, please notify
              Unacademy of the same.
            </P1>
            <a name={tableData[10].id}>
              <StyledH3>{tableData[10].label}</StyledH3>
            </a>
            <P1>
              You will not without obtaining prior written consent of Unacademy,
              disclose to third party any Confidential Information (as defined
              below) that is disclosed to you during the term of your use of the
              Services. For the purpose of this clause Confidential Information
              shall include but shall not be limited to employee details, User
              list, business model, processes, ideas, concepts etc. relating to
              the Service or Unacademy Platform which are not available in the
              public domain. You acknowledge and agree that the Confidential
              Information so provided to you shall at all times the property of
              Unacademy and any breach of the same shall cause irreparable
              damage to us.
            </P1>
            <a name={tableData[11].id}>
              <StyledH3>{tableData[11].label}</StyledH3>
            </a>
            <P1>
              You acknowledge that any use of the Services contrary to these
              Service Terms, or any transfer, sublicensing, copying or
              disclosure of technical information or materials related to the
              Services, may cause irreparable injury to Unacademy, and under
              such circumstances Unacademy, Unacademy will be entitled to
              equitable relief, without posting bond or other security,
              including, but not limited to, preliminary and permanent
              injunctive relief.
            </P1>
            <a name={tableData[12].id}>
              <StyledH3>{tableData[12].label}</StyledH3>
            </a>
            <P1>
              <b>NO WARRANTIES.</b>
              <br />
              YOU UNDERSTAND AND AGREE THAT THE SERVICES ARE PROVIDED &quot;AS
              IS&quot; AND UNACADEMY EXPRESSLY DISCLAIMS ALL WARRANTIES OF ANY
              KIND, EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY
              WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE OR
              NON-INFRINGEMENT. UNACADEMY MAKES NO WARRANTY OR REPRESENTATION
              REGARDING THE RESULTS THAT MAY BE OBTAINED FROM THE USE OF THE
              SERVICES, REGARDING THE ACCURACY OR RELIABILITY OF ANY INFORMATION
              OBTAINED THROUGH THE SERVICES OR THAT THE SERVICES WILL MEET ANY
              USER&apos;S REQUIREMENTS, OR BE UNINTERRUPTED, TIMELY, SECURE OR
              ERROR FREE. USE OF THE SERVICES IS AT YOUR SOLE RISK. ANY MATERIAL
              AND/OR DATA DOWNLOADED OR OTHERWISE OBTAINED THROUGH THE USE OF
              THE SERVICES IS AT YOUR OWN DISCRETION AND RISK. YOU WILL BE
              SOLELY RESPONSIBLE FOR ANY DAMAGE TO YOU RESULTING FROM THE USE OF
              THE SERVICES. THE ENTIRE RISK ARISING OUT OF USE OR PERFORMANCE OF
              THE SERVICES REMAINS WITH YOU. UNACADEMY DOES NOT ASSUME ANY
              RESPONSIBILITY FOR RETENTION OF ANY USER INFORMATION OR
              COMMUNICATIONS BETWEEN USERS. UNACADEMY CANNOT GUARANTEE AND DOES
              NOT PROMISE ANY SPECIFIC RESULTS FROM USE OF THE SERVICES. USE IS
              AT YOUR OWN RISK.
            </P1>
            <br />
            <P1>
              <b>Indemnity.</b>
              You agree to indemnify, defend and hold harmless Unacademy its
              officers, directors, employees, consultants and agents, from any
              and all third party claims, liability, damages and/or costs
              (including, but not limited to, attorneys&apos; fees) arising from
              Your use of the Services, Your violation of these Service Terms or
              the infringement or violation by You or any other user of Your
              account, of any intellectual property or other right of any person
              or entity or applicable law.
            </P1>
            <a name={tableData[13].id}>
              <StyledH3>{tableData[13].label}</StyledH3>
            </a>
            <P1>
              TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT SHALL
              UNACADEMY, ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS, BE
              LIABLE TO YOU FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
              PUNITIVE, LOSSES OR EXPENSES OR CONSEQUENTIAL DAMAGES WHATSOEVER
              RESULTING FROM ANY (I) ERRORS, MISTAKES, OR INACCURACIES IN THE
              SERVICES (II) PERSONAL INJURY OR PROPERTY DAMAGE, OF ANY NATURE
              WHATSOEVER, RESULTING FROM YOUR ACCESS TO AND USE OF THE SERVICES,
              (III) ANY UNAUTHORIZED ACCESS TO OR USE OF OUR SECURE SERVERS
              AND/OR ANY AND ALL PERSONAL INFORMATION AND/OR FINANCIAL
              INFORMATION STORED THEREIN, (IV) ANY INTERRUPTION OR CESSATION OF
              TRANSMISSION TO OR FROM THE SERVICES, (IV) ANY BUGS, VIRUSES,
              TROJAN HORSES, OR THE LIKE, WHICH MAY BE TRANSMITTED TO OR THROUGH
              THE SERVICES BY ANY THIRD PARTY, AND/OR (V) ANY ERRORS OR
              OMISSIONS IN ANY USER CONTENT OR ANY OTHER CONTENT OR FOR ANY LOSS
              OR DAMAGE OF ANY KIND INCURRED AS A RESULT OF YOUR USE OF ANY
              CONTENT POSTED, EMAILED, TRANSMITTED, OR OTHERWISE MADE AVAILABLE
              VIA THE SERVICES, WHETHER BASED ON WARRANTY, CONTRACT, TORT, OR
              ANY OTHER LEGAL THEORY, AND WHETHER OR NOT UNACADEMY IS ADVISED OF
              THE POSSIBILITY OF SUCH DAMAGES.
            </P1>
            <P1>
              WE UNDERSTAND THAT, IN SOME JURISDICTIONS, WARRANTIES, DISCLAIMERS
              AND CONDITIONS MAY APPLY THAT CANNOT BE LEGALLY EXCLUDED, IF THAT
              IS TRUE IN YOUR JURISDICTION, THEN TO THE EXTENT PERMITTED BY LAW,
              UNACADEMY LIMITS ITS LIABILITY FOR ANY CLAIMS UNDER THOSE
              WARRANTIES OR CONDITIONS TO SUPPLYING YOU THE SERVICES AGAIN.
            </P1>
            <P1>
              YOU SPECIFICALLY ACKNOWLEDGE THAT UNACADEMY SHALL NOT BE LIABLE
              FOR USER CONTENT OR THE DEFAMATORY, OFFENSIVE, OR ILLEGAL CONDUCT
              OF ANY THIRD PARTY AND THAT THE RISK OF HARM OR DAMAGE FROM THE
              FOREGOING RESTS ENTIRELY WITH YOU.
            </P1>
            <P1>
              THE SERVICES ARE CONTROLLED AND OFFERED BY UNACADEMY FROM ITS
              FACILITIES IN INDIA. UNACADEMY MAKES NO REPRESENTATIONS THAT THE
              SERVICES ARE APPROPRIATE OR AVAILABLE FOR USE IN OTHER LOCATIONS.
              THOSE WHO ACCESS OR USE THE SERVICES FROM OTHER JURISDICTIONS DO
              SO AT THEIR OWN VOLITION AND ARE RESPONSIBLE FOR COMPLIANCE WITH
              LOCAL LAW.
            </P1>
            <a name={tableData[14].id}>
              <StyledH3>{tableData[14].label}</StyledH3>
            </a>
            <ol>
              <li>
                Communications: We may send you promotions or otherwise
                communicate with you electronically, which may include e-mail,
                push notification, and you hereby consent to receive those
                communications. These communications will be in accordance with
                the Unacademy Privacy Policy.
              </li>
              <li>
                Modification: Unacademy reserves the right to modify, suspend,
                or discontinue the Services, or any part thereof, at any time
                and without notice (except as required by applicable law), and
                Unacademy will not be liable to you should it exercise such
                rights, even if your ability to use the Services is impacted by
                the change.
              </li>
              <li>
                Amendments: Unacademy reserves the right to make changes to
                these Service Terms at any time by posting the revised terms in
                connection with Services. To the maximum extent permitted by
                law, your continued use of the Services following any changes
                will constitute your acceptance of such changes.
              </li>
              <li>
                Waiver. Our failure to insist upon or enforce your strict
                compliance with these Service Terms will not constitute a waiver
                of any of our rights.
              </li>
              <li>
                Disputes/Conditions of Use. Any dispute or claim arising from or
                relating to these Service Terms or the Services is subject to
                the governing law, disclaimer of warranties and limitation of
                liability, any binding arbitration, and all other terms as
                mentioned in the Unacademy Terms of Use. You agree to those
                terms along with the Policies by using the Services.
              </li>
              <li>
                Severability: If any term or condition of these Service Terms
                are deemed invalid, void, or for any reason unenforceable, that
                part will be deemed severable and will not affect the validity
                and enforceability of any remaining term or condition.
              </li>
            </ol>

            <br />
            <br />
          </ClassroomTermsContainer>
        </ClassroomTermsWrapper>
      </RightContainer>
    </>
  );
};

ClassroomTerms.pageInfo = {
  name: 'Unacademy Classroom Terms',
  isMWebSupported: true,
  layoutOptions: {
    disableDarkMode: true
  }
};

export default ClassroomTerms;
ClassroomTerms.Layout = Layout;
ClassroomTerms.Wrapper = PageReduxProvider;
